import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';
import cloneDeep from 'lodash/cloneDeep';
import clone from 'lodash/clone';
import get from 'lodash/get';
import isPlainObject from 'lodash/isPlainObject';
import escapeRegExp from 'lodash/escapeRegExp';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import EE from 'eventemitter3';

import { FormContext, defaultFormContext } from './FormContext';
import FormEdit from './FormEdit';
import FormManager from './FormManager';

import FormParagraph from './control/FormParagraph';
import FormInputItem from './control/FormInputItem';
import FormInputSelectItem from './control/FormInputSelectItem';
import FormInputTextareaItem from './control/FormInputTextareaItem';
import FormInputButton from './control/FormInputButton';
import FormInputCheckbox from './control/FormInputCheckbox';

const replaceBars = function(str, lookup) {
  if (lookup) {
    const args = Object.keys(lookup);
    for (const key of args) {
      if (lookup[key]) {
        str = str.replace(new RegExp('\\{' + key + '\\}', 'gi'), lookup[key].data);
      }
    }
  }

  return str;
};

const _ = {
  clone,
  cloneDeep,
  throttle,
  get,
  isEqual,
  isPlainObject,
};
/*
  TODO: Eliminate itemId confusion.

  TODO: Streamline the handling up of backend responses that result in actions.
*/

class BlazeForm extends Component {
  constructor(props) {
    super(props);

    // Form Manager used to help keep the form object in order
    this.manager = props.manager || new FormManager();
    this.ee = new EE();

    this.manager.prefix = props.usePrefix;
    this.manager.itemId = props.itemId.data;
    this.manager.action = props.action;

    // Form data has been initialized
    this.initialized = false;

    this.state = {
      key: 0,
      form: {},
      itemId: props.itemId,
      saving: false,
      sendReport: false,
      layout: [],
      enabledControls: true,
    };

    // register default controls
    const controls = {
      Input: FormInputItem,
      Select: FormInputSelectItem,
      Textarea: FormInputTextareaItem,
      Button: FormInputButton,
      Paragraph: FormParagraph,
      Checkbox: FormInputCheckbox,
    };

    for (const name of Object.keys(controls)) {
      this.manager.registerControl({ name, component: controls[name] });
    }

    this.formContext = {
      ...defaultFormContext,
      ee: this.ee,
      focusFirstField: props.focusFirstField,
      handleAddItem: this.handleAddItem.bind(this),
      handleChange: this.handleChange.bind(this),
      handleSubmit: this.handleSubmit.bind(this),
      hideCancel: props.hideCancel,
      hideReset: props.hideReset,
      hideSubmit: props.hideSubmit,
      hideToolbar: props.hideToolbar,
      manager: this.manager,
      onTableFilter: props.onTableFilter,
      onValidationReport: props.onValidationReport,
      toggleControls: this.toggleControls.bind(this),
      trackDirty: props.trackDirty,
    };

    this.currentStep = 0;

    // Basic crud handlers
    this.handleBlur = this.handleBlur.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleFormClick = this.handleFormClick.bind(this);
    this.handleRemoveItem = this.handleRemoveItem.bind(this);
    this.handleResetForm = this.handleResetForm.bind(this);
    this.handleRowChange = this.handleRowChange.bind(this);
    this.handleWrapperClick = this.handleWrapperClick.bind(this);

    this.setWarning = this.setWarning.bind(this);
    this.setInfo = this.setInfo.bind(this);
    this.previousStep = this.previousStep.bind(this);
    this.checkAll = this.checkAll.bind(this);

    this.throttleSubmit = _.throttle(this.throttleSubmit.bind(this), 500, { trailing: false });
    this.handleSubmit = this.handleSubmit.bind(this);

    // Track field values in which configured update events were executed
    this.updateHistory = {};

    // Validation Report Addendum (validation failures not from validator
    this.reportAddendum = {};

    this.FormEdit = React.createRef();
  }

  componentDidMount() {
    const { layout } = this.props;

    if (layout.length > 0) {
      this.initLayout(layout);
    } else {
      console.error('FEC requires a layout on mount.', layout);
    }
  }

  // useEffect basically
  shouldComponentUpdate(newProps) {
    this.manager.action = newProps.action;
    return true;
  }

  /**
   * Initializes form data.  Most of the work is done in the FormManager.
   * This includes setting appropriate display values based on validation results
   * as well as data alternates as defined by the page layout.
   *
   * @param {Object} layout - Configuration definitions.
   * @param {Boolean} skipInitialValues - Flag to skip populating of initial values
   * @return {Object} form object literal.
   */

  /* eslint-disable react/sort-comp */
  initLayout(layout, skipInitialValues = false) {
    const { initialValues } = this.props;
    this.initialized = false;
    this.manager.load(layout).init();
    this.layout = this.manager.layout;

    this.setState({ layout: this.layout.getLayout() });

    if (!skipInitialValues) {
      Object.keys(initialValues).forEach(fieldName => {
        this.manager.setValue(fieldName, initialValues[fieldName]);
      });
    }

    let form = this.manager.getData();

    // Forms that are new or copied will have fields all set to dirty to ensure
    // they are sent with the submission and are validated
    if (this.isCreationAction()) {
      form = this.manager.dirtyAll().getData();
    } else {
      delete form.id;
    }

    // Save initial state after initialize in memory (for reset form)
    this.initialState = _.cloneDeep(form);

    this.syncForm(form);

    this.initialized = true;

    return form;
  }

  /**
   * Determines what type of action the form is making.
   *
   * @return {Boolean}
   */
  isCreationAction() {
    const { action } = this.props;

    return action === 'new' || action === 'copy';
  }

  /**
   * Adds a new data point to a collection.
   *
   * @param {String} tableName - Table identifier.
   * @param {Number} newIndex
   * @return {Object} - form object literal.
   */
  handleAddItem(tableName, newIndex) {
    const tableManager = this.manager.tables[tableName];
    const form = this.manager
      .addRow(tableName, newIndex)
      .validate()
      .getData();

    // TODO Manage 'masterForm' concept better
    tableManager.masterForm = form;
    const newRowId = tableManager.addNewId(newIndex);

    // TODO Nice if this was included with addRow method instead
    // but executeUpdateRules needs to be moved there first
    for (const fieldName of tableManager.fieldNameList) {
      const field = form[fieldName];
      if (field.autoPopulate && field.events && field.events.blur) {
        if (field.data[newIndex] !== '') {
          this.executeUpdateRules(fieldName, 'blur', newRowId);
        }
      }
    }

    this.syncForm();

    return this.manager.getData();
  }

  /**
   * Removes a data point from a collection.
   *
   * @param {String} tableName - Table identifier.
   * @param {Number} removeIndex - The index that needs to be removed.
   * @return {Object} - form object literal.
   */
  handleRemoveItem(tableName, removeIndex) {
    const tableManager = this.manager.getTableManager(tableName);
    const tableConfig = this.manager.getTableConfig(tableName);
    // const form = this.manager.getData();

    this.manager.removeRow(tableManager.fieldNameList, removeIndex).validate();

    // Execute update rules... probably needs to be an event for onRemove instead
    // for (const fieldName of tableManager.fieldNameList) {
    //   const field = form[fieldName];
    //   if (field.events && field.events.blur) {
    //     this.executeUpdateRules(fieldName, 'blur');
    //   }
    // }

    if (tableConfig.events && tableConfig.events.remove) {
      this.executeTableUpdateRules(tableName, 'remove');
    }

    this.syncForm();

    return this.manager.getData();
  }

  /**
   * Basically a wrapper for handleChange for any elements in a collection.
   * This makes an entire row dirty with any change.  Unfortunately, this makes the
   * entire collection dirty currently.
   *
   * TODO: handleChange should be able to handle this logic itself
   *
   * @param {Array} fieldList - A list of field objects
   * @param {Number} dataIndex - The index at which data was changed.
   * @param {String} name - The name of the field that was changed.
   * @param {String} value - The new value of the field that was changed.
   * @param {Function} cb - Callback function.
   * @return {Object} - form object literal.
   */
  handleRowChange(fieldList, dataIndex, fieldName, value, cb) {
    // console.debug('Parent handleRowChange', fieldList, dataIndex, value, cb);
    // const form = this.handleChange(name, value);
    const form = this.manager.getData();
    const field = form[fieldName];
    const rowId = this.manager.tables[field.insideTable].getRowIdByDataIndex(dataIndex);
    const oldValue = field.data[dataIndex];
    field.data[dataIndex] = value;
    this.manager.setValue(fieldName, field.data); // Unnecessary?

    if (!Array.isArray(fieldList)) {
      fieldList = [fieldList];
    }

    fieldList.forEach(fname => {
      form[fname].dirty = true;
      // TODO: Rather avoid doing this as it'll make everything in the collection update,
      // but it's required for submit to update fields
      form[fname].needsValidation = true;
    });

    const { events } = field;
    if (events && events.blur && events.blur.update) {
      this.setState({ enabledControls: false });
    }

    if (field.control === 'checkbox' && oldValue !== value) {
      if (events && events.check && events.check.update) {
        const options = field.optionsIndex[dataIndex];
        const isChecked = options[0] === value;
        if (isChecked) {
          this.executeUpdateRules(fieldName, 'check', rowId);
        } else {
          this.executeUpdateRules(fieldName, 'uncheck', rowId);
        }
      }
    }

    // TODO May not need to do syncForm at all here and just the callback
    if (cb) {
      this.syncForm(form, cb);
    }

    this.props.onChange({
      name: fieldName,
      value,
      dataIndex,
      rowId,
      manager: this.manager,
      updateForm: this.updateForm.bind(this),
      form,
    });

    return form;
  }

  handleFormClick = event => {
    const { blazeSubmit, blazeReset } = event.target.dataset;

    if (blazeSubmit) {
      this.handleSubmit(event);
    }

    if (blazeReset) {
      this.resetForm();
    }
  };

  /**
   * Click handler for any defined fields.
   *
   * @param {String} fieldName - The name of the field that was changed.
   * @param {Number} dataIndex - The index at which a blur occurred.
   * @return {Object} - form object literal.
   */
  handleClick(fieldName, dataIndex, rowId) {
    // console.debug(`Calling Parent handleClick on field:${fieldName}`);
    const form = this.manager.getData();
    const { events } = form[fieldName];

    // Do nothing if click events are not defined
    if (!events || !events.click) {
      return form;
    }

    if (events.click instanceof Function) {
      return events.click({ fieldName, dataIndex, rowId, manager: this.manager });
    }

    // Update rules
    if (events.click.update) {
      this.syncForm(this.executeUpdateRules(fieldName, 'click', rowId));
    }

    // Launch modal or terminal
    if (events.click.launch) {
      const { helper, ...options } = events.click.launch;

      // See if lookupKey points to a field name so that it's data can be injected
      let { lookupKey, query } = events.click.launch;
      if (lookupKey && form[lookupKey]) {
        lookupKey =
          Array.isArray(form[lookupKey].data) && dataIndex !== undefined
            ? form[lookupKey].data[dataIndex]
            : form[lookupKey].data;
      }

      // Fill in query
      if (query && isPlainObject(query)) {
        Object.keys(query).forEach(formFieldName => {
          query[formFieldName] = replaceBars(query[formFieldName], form);
        });
      }

      // Include built in like browser / new window?
      this.props.onLaunch({ ...options, data: lookupKey, helper, fieldName, dataIndex, rowId });
    }

    return form;
  }

  /**
   * Click handler for a field's wrapper.
   *
   * @param {String} fieldName - The field name whose wrapper was clicked.
   */
  handleWrapperClick(fieldName) {
    const form = this.manager.getData();

    if (form[fieldName].clickToEdit) {
      if (form[fieldName].disabled === true) {
        form[fieldName].disabled = false;
        this.syncForm();
      }
    }
  }

  /**
   * Any changes for a specific form should go through this handler.
   *
   * @param {String} fieldName - The name of the field that was changed.
   * @param {String} value - The new value of the field that was changed.
   * @param {Function} callback - Callback
   * @return {Object} - form object literal.
   */
  handleChange(fieldName, value, callback) {
    // console.debug(`Calling Parent handleChange on field:${name} with value: ${value}`);
    this.manager.setValue(fieldName, value);

    const form = this.manager.getData();
    const field = form[fieldName];

    const { events } = field;

    if (events && events.blur && events.blur.update) {
      this.setState({ enabledControls: false });
    }

    if (field.control === 'checkbox') {
      if (events && events.check && events.check.update) {
        const options = field.options;
        const isChecked = options[0] === value;
        if (isChecked) {
          this.executeUpdateRules(fieldName, 'check');
        } else {
          this.executeUpdateRules(fieldName, 'uncheck');
        }
      }
    }

    callback && callback(); // eslint-disable-line
    // this.syncForm(form, cb);

    this.props.onChange({
      name: fieldName,
      value,
      manager: this.manager,
      updateForm: this.updateForm.bind(this),
      form,
    });

    return form;
  }

  /**
   * Any blur even on the form should go through this handler. Validation and
   * arbitrary field rules and dependencies are also evaluated and acted upon.
   *
   * @param {String} fieldName - The name of the field that was changed.
   * @param {Number} dataIndex - The index at which a blur occurred.
   * @return {Object} - Any changes to the given fieldName.
   */
  handleBlur(fieldName, dataIndex, rowId) {
    // console.debug(`Calling Parent handleBlur on field: ${fieldName} with dataIndex: ${dataIndex}`);

    this.manager.addHistory(fieldName);

    this.clearHighlight(fieldName);

    // this.manager.setData(this.state.form).touch(fieldName, dataIndex).validate();
    let form = this.manager.touch(fieldName, dataIndex).getData();

    const { events } = form[fieldName];

    if (events && events.blur && events.blur instanceof Function) {
      events.blur({
        fieldName,
        dataIndex,
        rowId,
        manager: this.manager,
        updateForm: this.updateForm.bind(this),
        ctx: this.formContext,
      });
    }

    form = this.executeUpdateRules(fieldName, 'blur', rowId);

    // TODO I can't just throw in currentStep right now because the default is set to 0 in constructor
    const step = this.layout.hasSteps ? this.currentStep : null;

    form = this.manager
      .setData(form)
      .validate(step)
      .getData();

    if (form[fieldName].clickToEdit && form[fieldName].data !== '') {
      form[fieldName].disabled = true;
    }

    this.syncForm(form);
    this.setState({ enabledControls: true });

    this.props.onBlur({
      name: fieldName,
      value: form[fieldName].data,
      dataIndex,
      rowId,
      manager: this.manager,
      updateForm: this.updateForm.bind(this),
    });

    if (dataIndex !== undefined) {
      return {
        rawValue: form[fieldName].data[dataIndex],
        displayValue: form[fieldName].displayValue[dataIndex],
        valid: form[fieldName].validIndex[dataIndex],
        validError: form[fieldName].errorIndex[dataIndex],
      };
    }

    return {
      rawValue: form[fieldName].data,
      displayValue: form[fieldName].displayValue,
      valid: form[fieldName].valid,
      validError: form[fieldName].errorIndex[0],
    };
  }

  clearHighlight(fieldName) {
    if (this.FormEdit && this.FormEdit.current && this.FormEdit.current.clearHighlight) {
      this.FormEdit.current.clearHighlight(fieldName);
    }
  }

  toggleControls(what) {
    this.setState({ enabledControls: what });
  }

  /**
   * Sets options for a specified field.
   *
   * @param {String} fieldName - The name of the field to set a options on.
   * @param {Array} options - The new options.
   * @param {Number} dataIndex - The index of a collection or table to set the options on.
   *
   * @return {Object} - Form object.
   */
  setOptions(fieldName, options, dataIndex) {
    const form = this.manager.getData();

    if (!form[fieldName]) {
      FormManager.warnFieldName(fieldName);
      return form;
    }

    // Static Options for search (options gets mutated here)
    // TODO Move this to options like everything else
    if (form[fieldName].validation && form[fieldName].validation.type === 'static') {
      if (Array.isArray(options)) {
        options.forEach((option, optionIndex) => {
          if (!_.isPlainObject(option)) {
            options[optionIndex] = { label: option, value: option };
          }
        });

        form[fieldName].validation.suggestions = options;
      } else {
        console.warn('Invalid Options given', options);
      }
    } else {
      if (dataIndex !== undefined) {
        form[fieldName].optionsIndex[dataIndex] = options;
      } else {
        form[fieldName].options = options;
        if (form[fieldName].insideTable) {
          form[fieldName].optionsIndex = form[fieldName].data.map(() => options);
        }
      }
    }

    return form;
  }

  /**
   * Sets a warning on a field.
   *
   * @param {String} fieldName - The name of the field to set a warning on.
   * @param {String} message - The warning text.
   * @param {Number} dataIndex - The index of a collection or table to set the warning on.
   *
   * @return {Object} - Form object.
   */
  setWarning(fieldName, message, dataIndex) {
    // console.debug(`Calling parent setWarning on field ${fieldName} with dataIndex: ${dataIndex}`);
    const form = this.manager.getData();
    const field = form[fieldName];

    if (!field) {
      FormManager.warnFieldName(fieldName);
      return form;
    }

    if (dataIndex !== undefined) {
      field.warningIndex[dataIndex] = message;
    } else {
      if (Array.isArray(message) && field.insideTable) {
        message.forEach((warning, index) => {
          field.warningIndex[index] = warning;
        });
      } else {
        field.warning = message;
      }
    }

    return form;
  }

  /**
   * Sets a info on a field, like warning but with a different class.
   *
   * @param {String} fieldName - The name of the field to set a info on.
   * @param {String} message - The info text.
   * @param {Number} dataIndex - The index of a collection or table to set the info on.
   *
   * @return {Object} - Form object.
   */
  setInfo(fieldName, message, dataIndex) {
    // console.debug(`Calling parent setWarning on field ${fieldName} with dataIndex: ${dataIndex}`);
    const form = this.manager.getData();
    const field = form[fieldName];

    if (!field) {
      FormManager.warnFieldName(fieldName);
      return form;
    }

    if (dataIndex !== undefined) {
      field.infoIndex[dataIndex] = message;
    } else {
      if (Array.isArray(message) && field.insideTable) {
        message.forEach((info, index) => {
          field.infoIndex[index] = info;
        });
      } else {
        field.info = message;
      }
    }

    return form;
  }

  executeTableUpdateRules(tableName, eventType) {
    const form = this.manager.getData();
    const tableConfig = this.manager.getTableConfig(tableName);

    if (!tableConfig) {
      return form;
    }

    const rules = _.get(tableConfig, `events.${eventType}.update`);

    if (!rules) {
      return form;
    }

    const updateThese = this.getUpdateObjectFromRules(rules);

    return this.updateForm(updateThese);
  }

  executeUpdateRules(fieldName, eventType, rowId, reRenderForm = false, skipValidation = false) {
    const form = this.manager.getData();
    const field = form[fieldName];
    let dataIndex;

    // Check for field
    if (!field) {
      FormManager.warnFieldName(fieldName);
      return form;
    }

    const rules = _.get(field, `events.${eventType}.update`);

    // Check for Rules
    if (!rules) {
      return form;
    }

    if (field.insideTable) {
      dataIndex = this.manager.tables[field.insideTable].getDataIndexByRowId(rowId);
    }

    // Check and update the updateHistory
    // Do not execute rules if this was invoked again without the value changing
    if (eventType === 'blur') {
      if (!field.insideTable) {
        // Check updateHistory if there is one, if not check to see if the value isn't the original value
        if (this.updateHistory[fieldName]) {
          if (this.updateHistory[fieldName] === field.data) {
            return form;
          }
        } else {
          if (field.originalValue === field.data) {
            return form;
          }
        }

        this.updateHistory[fieldName] = form[fieldName].data;
      } else {
        const computedName = `${fieldName}-${rowId}`;
        if (this.updateHistory[computedName]) {
          if (this.updateHistory[computedName] === form[fieldName].data[dataIndex]) {
            return form;
          }
        }

        this.updateHistory[computedName] = form[fieldName].data[dataIndex];
      }
    }

    // Get Update Object from defined rules
    const updateThese = this.getUpdateObjectFromRules(rules, fieldName, dataIndex, rowId, reRenderForm);

    return this.updateForm(updateThese, dataIndex, rowId, reRenderForm, skipValidation);
  }

  getUpdateObjectFromRules(rules, fieldName, dataIndex, rowId, reRenderForm) {
    const form = this.manager.getData();

    const updateThese = {};
    rules.forEach(rule => {
      if (rule.replaceFieldNameKeys) {
        rule.replaceFieldNameKeys(this.state.itemId.data);
      }
      if (rule.method) {
        this.props.onUpdateRule({
          rule,
          fieldName,
          dataIndex,
          rowId,
          reRenderForm,
        });
      } else {
        ['value', 'options', 'list'].forEach(fieldKey => {
          if (rule[fieldKey] === undefined) {
            return;
          }

          rule.field.forEach(name => {
            // Skip Invalid fieldName and print an error
            if (!form[name]) {
              FormManager.warnFieldName(name);
              return;
            }

            if (fieldKey === 'value') {
              // The specified value matches a field name, so use that field's data as the value
              if (form[rule[fieldKey]]) {
                if (form[rule[fieldKey]].insideTable && dataIndex !== undefined) {
                  updateThese[name] = {
                    value: form[rule[fieldKey]].data[dataIndex],
                  };
                } else {
                  updateThese[name] = {
                    value: form[rule[fieldKey]].data,
                  };
                }

                return;
              }

              updateThese[name] = {
                value: rule[fieldKey],
                allRows: rule.allRows,
                rowIndex: rule.rowIndex,
              };

              return;
            }

            // TODO Same as initBlurOptions() in FM
            if (fieldKey === 'options') {
              if (form[rule[fieldKey]].insideTable || form[rule[fieldKey]].collection) {
                const options = [];
                form[rule[fieldKey]].data.forEach((v, idx) => {
                  options.push({
                    label: form[rule[fieldKey]].displayValue[idx],
                    value: form[rule[fieldKey]].data[idx],
                  });
                });
                updateThese[name] = {
                  [fieldKey]: options,
                };
              } else {
                updateThese[name] = {
                  [fieldKey]: [
                    {
                      label: form[rule[fieldKey]].displayValue,
                      value: form[rule[fieldKey]].data,
                    },
                  ],
                };
              }

              return;
            }

            if (fieldKey === 'list') {
              if (rule[fieldKey].method && rule[fieldKey].method.keys) {
                rule[fieldKey].method.keyData = [];
                rule[fieldKey].method.keys.forEach(key => {
                  if (!form[key]) {
                    console.warn(`Invalid key given ${key}`);
                  }
                  rule[fieldKey].method.keyData.push(form[key].data);
                });
              }
            }
          });
        });
      }
    });

    return updateThese;
  }

  /**
   * Clears a field's value.  Uses event emitter.
   *
   * @param {String} fieldName - The name of the field to clear.
   *
   * @return {Object} - Form object.
   */
  clearField(fieldName) {
    const form = this.manager.getData();

    if (!form[fieldName]) {
      this.manager.warnFieldName(fieldName);
      return;
    }

    this.ee.emit(form[fieldName].identifier, { action: 'clear' });

    return form;
  }

  /**
   * Method to update the form data or metadata outside normal user interaction.  It will
   * either do so through a re-render or through event emitter.
   *
   * @param {Object} update - Update key/value pair.  fieldName => fieldValue
   *  The fieldValue can also be an object to update other metadata outside of raw value.
   * @param {Number} dataIndex - The index as a point of reference for array/table values.
   * @param {Number} rowId - The rowId as a point of reference for table values.
   * @param {Boolean} reRenderForm - Flag to re-render when the update is complete.
   *
   * @return {Object} - Any changes to the given fieldName.
   */
  updateForm(update = {}, dataIndex, rowId, reRenderForm = false, skipValidation = false) {
    const { itemId } = this.state;
    const form = this.manager.getData();
    const fieldsToUpdate = [];
    const tablesToReload = [];

    Object.keys(update).forEach(fieldName => {
      let payload = update[fieldName];

      // Replace any #KEY# strings in the response (I wish we didn't have to do this)
      if (this.isCreationAction()) {
        fieldName = fieldName.replace('#KEY#', '');
      } else {
        if (itemId.data === undefined) {
          console.warn('The itemId is not defined, so the #KEY# replacement failed', this.state.itemId);
        }
        fieldName = fieldName.replace('#KEY#', itemId.data);
      }

      // Given field name doesn't exist in the form
      if (!form[fieldName]) {
        FormManager.warnFieldName(fieldName);
        return;
      }

      const field = form[fieldName];

      // Get rowId if it's not given
      // Checking if the table is registered in manager, because hidden tables aren't getting registered
      if (!rowId && dataIndex !== undefined && field.insideTable && this.manager.tables[field.insideTable]) {
        rowId = this.manager.tables[field.insideTable].getRowIdByDataIndex(dataIndex);
      }

      // The response can have values 'newData' or { value: newData }
      // if options are to be updated, it must be in the form of { options: newOptions}
      if (!_.isPlainObject(payload)) {
        payload = { value: payload };
      }

      const fieldId = field.identifier;

      // Too much to update through event emitter
      if (Object.keys(payload).length > 10) {
        reRenderForm = true;
      } else if (Array.isArray(payload.value) && payload.value.length > 50 && field.insideTable) {
        reRenderForm = true;
        // TODO The specific table in question needs to re-render (not the whole form)
      } else if (field.insideTable && payload.options && dataIndex === undefined) {
        reRenderForm = true;

        // TODO see if this can go away at some point
      } else if (field.insideTable && payload.options && field.validation && field.validation.type === 'static') {
        reRenderForm = true;
      }

      const { rowIndex, allRows, ...otherFieldKeys } = payload;

      // fieldKeys can be value, options, info, warning
      Object.keys(otherFieldKeys).forEach(fieldKey => {
        const newValue = payload[fieldKey];

        // Regular Update to non-table field
        if (!field.insideTable) {
          fieldsToUpdate.push({
            controlId: fieldId,
            fieldKey,
            fieldName,
            newValue,
          });

          return;
        }

        // Beyond this point we are updating tables

        if (allRows && fieldKey === 'value') {
          this.columnUpdate(fieldName, newValue);
          return;
        }

        let computedRowId = rowId;
        let useDataIndex = dataIndex;
        if (rowIndex !== undefined && this.manager.tables[field.insideTable]) {
          computedRowId = this.manager.tables[field.insideTable].getRowIdByDataIndex(rowIndex);
          useDataIndex = rowIndex;
        }

        // A dataIndex is not specified and the value is not an Array
        // so each dataIndex will be updated with the same data
        // TODO: This is not good for updating a list of a set of components

        if (!Array.isArray(newValue)) {
          if (useDataIndex === undefined) {
            field.data.forEach((value, index) => {
              fieldsToUpdate.push({
                controlId: `${fieldId}-${index}`,
                fieldKey,
                fieldName,
                dataIndex: index,
                newValue: value,
              });
            });
          } else {
            fieldsToUpdate.push({
              controlId: `${fieldId}-${computedRowId}`,
              fieldKey,
              fieldName,
              dataIndex: useDataIndex,
              newValue,
            });
          }
        } else {
          // The field being updated is part of a table
          // and includes a new values in the form of an array, so it needs to
          // update each value for the field
          if (fieldKey === 'value' || fieldKey === 'warning' || fieldKey === 'info') {
            if (newValue.length === 0) {
              // reRenderForm = true;
              tablesToReload.push(field.insideTable);

              fieldsToUpdate.push({
                fieldKey,
                fieldName,
                newValue,
              });

              return;
            }

            if (newValue.length !== field.data.length) {
              // reRenderForm = true;
              tablesToReload.push(field.insideTable);
            }

            newValue.forEach((value, index) => {
              let thisRowId = index;
              if (this.manager.tables[field.insideTable]) {
                thisRowId = this.manager.tables[field.insideTable].getRowIdByDataIndex(index);
              }
              fieldsToUpdate.push({
                controlId: `${fieldId}-${thisRowId}`,
                fieldKey,
                fieldName,
                dataIndex: index,
                newValue: value,
              });
            });

            return; // eslint-disable-line
          }

          if (fieldKey === 'options') {
            fieldsToUpdate.push({
              controlId: `${fieldId}-${computedRowId}`,
              fieldKey,
              fieldName,
              dataIndex: useDataIndex,
              newValue,
            });

            return;
          }

          field.data.forEach((value, index) => {
            fieldsToUpdate.push({
              controlId: `${fieldId}-${index}`,
              fieldKey,
              fieldName,
              dataIndex: index,
              newValue,
            });
          });
        }
      });
    });

    return this.publishChanges(fieldsToUpdate, tablesToReload, reRenderForm, skipValidation);
  }

  publishChanges(changes = [], tablesToReload = [], reRenderForm, skipValidation) {
    const form = this.manager.getData();

    if (!changes.length && !tablesToReload.length) {
      return form;
    }

    changes.forEach(control => {
      const field = form[control.fieldName];

      if (!field) {
        FormManager.warnFieldName(control.fieldName);
        return;
      }

      switch (control.fieldKey) {
        case 'value': {
          if (control.dataIndex !== undefined) {
            // TODO needs to use manager setValue but it doesn't yet support dataIndex/table data
            field.data[control.dataIndex] = control.newValue;
            field.displayValue[control.dataIndex] = control.newValue;
            field.needsValidation = true;

            // TODO Should live in manager -- probably should check the index first
            field.hiddenIndex = _.clone(field.data).fill(!!field.hidden);
            field.disabledIndex = _.clone(field.data).fill(!!field.disabled);
            field.calculatedIndex = _.clone(field.data).fill(!!field.calculated);
            field.touchedIndex = _.clone(field.data).fill(false);
            field.warningIndex = _.clone(field.data).fill('');
          } else {
            this.manager.setValue(field.name, control.newValue);
            // field.data = control.newValue;
            // field.displayValue = control.newValue;
          }
          break;
        }

        case 'displayValue': {
          if (control.dataIndex !== undefined) {
            field.displayValue[control.dataIndex] = control.newValue;
          } else {
            field.displayValue = control.newValue;
          }
          break;
        }

        case 'options': {
          this.setOptions(control.fieldName, control.newValue, control.dataIndex);
          break;
        }

        case 'warning': {
          this.setWarning(control.fieldName, control.newValue, control.dataIndex);
          break;
        }

        case 'info': {
          this.setInfo(control.fieldName, control.newValue, control.dataIndex);
          break;
        }

        // no default
      }
    });

    // Validation needed to sanitize new values
    if (!skipValidation) {
      const step = this.layout.hasSteps ? this.currentStep : null;
      this.manager.validate(step);
    }

    // Publish if we aren't re-rendering
    if (!reRenderForm) {
      changes.forEach(control => {
        const field = form[control.fieldName];
        let { newValue } = control;

        if (control.fieldKey === 'value') {
          if (control.dataIndex !== undefined) {
            newValue = field.displayValue[control.dataIndex];
          } else {
            newValue = field.displayValue;
          }
        }

        this.ee.emit(control.controlId, {
          action: 'update',
          payload: {
            [control.fieldKey]: newValue,
          },
        });
      });

      this.reloadTables(tablesToReload);

      this.syncForm(form);
    }

    if (reRenderForm) {
      this.manager
        .initAllTables()
        .initAllOptions()
        .initAllOptionValues()
        .initBlurOptions();

      this.syncForm(form, this.incrementKey);
    }

    return form;
  }

  reloadTables(tablesToReload = []) {
    const form = this.manager.getData();
    [...new Set(tablesToReload)].forEach(tableId => {
      this.manager.initTable(tableId);

      const tableForm = {};
      this.manager.layout.getAllFieldsUnderGroup(tableId).forEach(fieldName => {
        tableForm[fieldName] = form[fieldName];
        this.manager.initOptions(fieldName);
        this.manager.initOptionValue(fieldName);
      });

      const tableManager = this.manager.tables[tableId];
      tableManager.masterform = tableForm;

      this.ee.emit(tableId, {
        action: 'reload',
      });
    });
  }

  /**
   * Actions to perform when submit is invoked.
   *
   * @return
   */
  async submitForm() {
    const { onSubmit, submitAll, exportFormat, useTypedData } = this.props;

    this.reportAddendum = {};

    // Set all data to dirty (so it gets sent)
    if (submitAll) {
      this.manager.dirtyAll();
    }

    // It has steps and isn't the last step
    const step = this.layout.hasSteps ? this.currentStep : null;

    // Run validation on data
    this.manager.touchAll(step).validate(step);
    const form = this.manager.getData();

    // Synchronize all form data
    this.syncForm();

    const isValid = this.layout.hasSteps ? this.manager.report.steps[step] : this.manager.report.valid;

    if (!isValid) {
      this.incrementKey();
      throw new Error('Validation failed');
    }

    // Gather form data in ajax ready format
    const exportedData =
      exportFormat === 'object' ? this.manager.exportDataObject(true, useTypedData) : this.manager.exportData(true);

    this.submitted = true;

    const response = await onSubmit(exportedData, {
      form,
      currentStep: this.currentStep,
    });

    // Clear fields that have clearOnSubmit set
    this.manager.clearFieldsThatNeedIt();
    // TODO Don't like this, but I just did it
    const data = this.manager.getData();
    Object.keys(data).forEach(fieldName => {
      if (data[fieldName].clearOnSubmit) {
        this.ee.emit(data[fieldName].identifier, { action: 'clear' });
      }
    });

    // Set all data as new because of a successful save
    this.syncForm(this.manager.pristineAll().getData());

    return response;
  }

  /**
   * Wrapper for submitForm()
   * Since submitForm is throttled by half a second, the saving state is changed
   * in handleSubmit so there is little to no delay.
   */
  handleSubmit(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.setState({ saving: true });

    return this.throttleSubmit();
  }

  async throttleSubmit() {
    const { onBeforeSubmit, resetOnSubmit } = this.props;

    // check for a before submit hook, if it returns false, short circuit submit
    // support sync / async methods
    if (onBeforeSubmit) {
      const form = this.manager.getData();
      const currentStep = this.currentStep;
      let result = onBeforeSubmit({ form, currentStep });

      if (result instanceof Promise) {
        result = await result;
      }

      if (result === false) {
        this.setState({ saving: false });
        return Promise.resolve(result);
      }
    }

    let response;
    try {
      if (this.state.enabledControls) {
        response = await this.submitForm();
        if (this.submitted) {
          // Reset form on successful submit if set
          if (resetOnSubmit) {
            this.handleResetForm();
          }
        }
      }

      return Promise.resolve({ submitted: true, response });
    } catch (err) {
      this.setState({ sendReport: true });
      return Promise.resolve({ submitted: false });
    } finally {
      this.setState({ saving: false });
    }
  }

  /**
   * Cancel button handler.
   *
   * @param {Object} event - Event object.
   * @return
   */
  handleCancel(event) {
    event.preventDefault();
    event.stopPropagation();
    this.props.onCancel();
  }

  /**
   * Force a re-render.
   *
   * @return
   */
  incrementKey(newState = {}) {
    this.manager.initAllTables(); // TODO: This probably shouldn't have to be here

    this.setState(prevState => ({ key: prevState.key + 1, ...newState }));
  }

  /**
   * Reset form data to its initial state.
   *
   * @return
   */
  handleResetForm() {
    if (this.props.onResetForm() === false) {
      return;
    }

    this.resetForm();
  }

  resetForm() {
    this.manager.setData(this.initialState);

    this.updateHistory = {};

    const newState = {
      form: this.initialState,
      sendReport: false,
    };

    if (this.layout.hasSteps) {
      this.currentStep = 0;
      newState.layout = this.layout.getSteps()[this.currentStep].fields;
      newState.key = 0;
    }

    this.submitted = false;
    this.incrementKey(newState);
  }

  columnUpdate(fieldName, newValue) {
    const form = this.manager.getData();
    const field = form[fieldName];
    const tableId = field.insideTable;

    if (!tableId) {
      console.warn(`Field ${fieldName} isn't inside a table`);
      return form;
    }
    const tableManager = this.manager.tables[tableId];

    if (!tableManager) {
      console.warn(`TableID ${tableId} appears to be invalid`);
      return form;
    }

    // const { start, end } = tableManager;

    // This callback executes update rules for all checked fields (if they are set)
    const callback = () => {
      // const currentForm = this.manager.getData();
      // currentForm[fieldName].data.forEach((val, dataIndex) => {
      //   const isRendered = dataIndex >= start && dataIndex < end;
      //   const skipValidation = !isRendered;
      //   const rowId = tableManager.getRowIdByDataIndex(dataIndex);
      //   this.executeUpdateRules(fieldName, 'blur', rowId, null, skipValidation);
      // });
      // this.syncForm(currentForm, () => {
      //   this.reloadTables([field.insideTable]);
      // });
    };

    field.data.forEach((currentValue, dataIndex) => {
      field.data[dataIndex] = newValue;
      field.displayValue[dataIndex] = newValue;
    });

    tableManager.forEachRendered(rowId => {
      const dataIndex = tableManager.getDataIndexByRowId(rowId);
      this.ee.emit(`${field.identifier}-${rowId}`, {
        action: 'update',
        payload: {
          value: field.displayValue[dataIndex],
        },
      });
    });

    this.syncForm(
      this.manager
        .setData(form)
        .validate()
        .getData(),
      callback
    );
  }

  /**
   * Check all for a column in a table.
   *
   * @param {String} fieldName - The field name associated with the column.
   * @param {Boolean} checked - The new checkbox state.
   * @param {Function} reload - Callback to reload table.
   * @return
   */
  checkAll(fieldName, checked, reload) {
    const form = this.manager.getData();
    const field = form[fieldName];
    const position = checked ? 0 : 1;
    const value = field.options[position];

    // Check All or Check All in Page
    const checkPage = !!form[fieldName].checkPage;
    const tableManager = this.manager.tables[field.insideTable];
    const { start, end } = tableManager;

    // This callback executes update rules for all checked fields (if they are set)
    const callback = () => {
      const currentForm = this.manager.getData();
      const checkEvent = checked ? 'check' : 'uncheck';

      if (checkPage) {
        tableManager.forEachRendered(rowId => {
          this.executeUpdateRules(fieldName, 'blur', rowId);
          this.executeUpdateRules(fieldName, checkEvent, rowId);
        });
      } else {
        currentForm[fieldName].data.forEach((val, dataIndex) => {
          const isRendered = dataIndex >= start && dataIndex < end;
          const skipValidation = !isRendered;
          const rowId = tableManager.getRowIdByDataIndex(dataIndex);
          this.executeUpdateRules(fieldName, 'blur', rowId, null, skipValidation);
          this.executeUpdateRules(fieldName, checkEvent, rowId, null, skipValidation);
        });
      }

      this.syncForm(currentForm, reload);
    };

    if (checkPage) {
      tableManager.forEachRendered(rowId => {
        const dataIndex = tableManager.getDataIndexByRowId(rowId);
        form[fieldName].data[dataIndex] = value;
        form[fieldName].displayValue[dataIndex] = value;
      });
    } else {
      form[fieldName].data.forEach((val, dataIndex) => {
        form[fieldName].data[dataIndex] = value;
        form[fieldName].displayValue[dataIndex] = value;
      });
    }

    // TODO May not need to setData
    this.syncForm(
      this.manager
        .setData(form)
        .validate()
        .getData(),
      callback
    );
  }

  /**
   * Go back a step. Optionally goto any step?
   *
   * @param {Number} step - Step to go back to.
   * @return
   */
  previousStep(step) {
    if (!step) {
      this.currentStep--;
    } else {
      this.currentStep = step;
    }

    this.manager.init(this.currentStep);

    this.incrementKey({
      sendReport: false,
      layout: this.layout.getSteps()[this.currentStep].fields,
    });
  }

  /**
   * Jump to the next step in a form with steps.
   *
   * @param {Object} changes - Used to prefill values for the next step.
   * @return
   */
  nextStep(changes = {}) {
    if (!_.isPlainObject(changes)) {
      console.log('Change object', changes);
      throw new Error('Invalid change object');
    }

    // useStep will explicitly goto a step otherwise it'll be incremented by 1
    if (changes.useStep) {
      this.currentStep = parseInt(changes.useStep, 10) - 1;
      delete changes.useStep;
    } else {
      this.currentStep++;
    }

    const form = this.manager.getData();

    const tablesToClear = [];
    // Clear tables to keep all data fresh
    Object.keys(changes).forEach(fieldName => {
      if (!form[fieldName]) {
        FormManager.warnFieldName(fieldName);
        return;
      }

      if (this.updateHistory[fieldName]) {
        delete this.updateHistory[fieldName];
      }

      const tableName = form[fieldName].insideTable;

      if (tableName) {
        if (!tablesToClear.includes(tableName)) {
          tablesToClear.push(tableName);
        }
      }
    });

    tablesToClear.forEach(tableName => {
      this.manager.clearTable(tableName);

      // TODO Needs to be a part of clear table, move "updateHistory" to manager as part of "history"
      this.manager.layout.getAllFieldsUnderGroup(tableName).forEach(fieldName => {
        Object.keys(this.updateHistory).forEach(f => {
          const re = new RegExp(`^${escapeRegExp(fieldName)}-`);
          if (re.test(f)) {
            delete this.updateHistory[f];
          }
        });
      });
    });

    // Set new values
    Object.keys(changes).forEach(fieldName => {
      if (!form[fieldName]) {
        FormManager.warnFieldName(fieldName);
        return;
      }

      if (_.isPlainObject(changes[fieldName])) {
        Object.keys(changes[fieldName]).forEach(key => {
          if (key === 'value') {
            this.manager.setValue(fieldName, changes[fieldName][key], true);
          } else if (key === 'options') {
            this.setOptions(fieldName, changes[fieldName][key]);
          } else if (key === 'warning') {
            this.setWarning(fieldName, changes[fieldName][key]);
          } else if (key === 'info') {
            this.setInfo(fieldName, changes[fieldName][key]);
          } else {
            form[fieldName][key] = changes[fieldName][key];
          }
        });
      } else {
        this.manager.setValue(fieldName, changes[fieldName], true);
      }
    });

    tablesToClear.forEach(tableName => {
      this.manager.initTable(tableName);

      // TODO maybe initTable should try to handle all this too?
      this.manager.layout.getAllFieldsUnderGroup(tableName).forEach(fieldName => {
        if (form[fieldName].aggregated) {
          return;
        }
        form[fieldName].disabledIndex = form[fieldName].data.map(() => !!form[fieldName].disabled);
        form[fieldName].hiddenIndex = form[fieldName].data.map(() => !!form[fieldName].hidden);
        form[fieldName].calculatedIndex = form[fieldName].data.map(() => !!form[fieldName].calculated);
      });
    });

    this.manager.init(this.currentStep);

    this.incrementKey({
      form: this.manager.getData(),
      layout: this.layout.getSteps()[this.currentStep].fields,
    });
  }

  /**
   * Indicates that the form is ready for editing.
   * Form state constructs as null and is initialized with initLayout()
   * @return {Boolean}
   */
  isReady() {
    return this.initialized && !_.isEqual(this.state.form, {});
  }

  /**
   * Indicates that loading is finished but the layout is empty or
   * there was a problem with it. initLayout() is complete but the
   * layout doesn't seem right.
   * @return {Boolean}
   */
  isValid() {
    if (this.state.form && this.props.layout.length === 0) {
      return false;
    }

    return true;
  }

  /**
   * Reset field in form manager and update form object in state
   * @param {String} value
   * @param {String} [dataIndex=undefined]
   */
  resetFieldValue(value, dataIndex) {
    this.manager.resetFieldValue(value, dataIndex);
    this.syncForm();
    this.incrementKey();
  }

  /**
   * Anything that might need to happen when the form is changed
   *
   * @param {Object} form - Form data object.
   * @param {function} cb - Callback function for when setState has completed
   * @return
   */
  syncForm(form, cb = () => {}) {
    if (!form) {
      form = this.manager.getData();
    }

    form = _.cloneDeep(form);
    this.setState({ form }, cb);

    // TODO: Experimental, might have perf implications
    this.ee.emit('syncForm', { action: 'sync', payload: form });
  }

  /**
   * Combines everything from validation report from Validator and
   * anything in the reportAddendum object.
   *
   * @return
   */
  compilePublicReport() {
    if (!this.state.sendReport) {
      return {};
    }

    const report = _.cloneDeep(this.manager.report);

    // Include addendum on validation report
    Object.keys(this.reportAddendum).forEach(fieldName => {
      if (!fieldName) {
        return;
      }

      if (!report.failed) {
        report.failed = {};
      }

      report.valid = false;

      const reason = this.reportAddendum[fieldName];
      if (report.failed[fieldName]) {
        report.failed[fieldName].errors.push({ reason });
      } else {
        report.failed[fieldName] = { errors: [{ reason }] };
      }
    });

    return report;
  }

  render() {
    const { renderLoading, renderInvalidLayout } = this.props;

    if (!this.isReady()) {
      return renderLoading();
    }

    if (!this.isValid()) {
      return renderInvalidLayout();
    }

    return (
      <FormContext.Provider
        value={{ ...this.formContext, enabledControls: this.state.enabledControls, isReadonly: this.props.readOnly }}
      >
        <FormEdit
          ref={this.FormEdit}
          key={this.state.key}
          action={this.props.action}
          cancelLabel={this.props.cancelLabel}
          checkAll={this.checkAll}
          className={this.props.className}
          currentStep={this.currentStep}
          customActions={this.props.customActions}
          dirty={this.manager.isDirty()}
          form={this.state.form}
          itemId={this.state.itemId}
          itemName={this.props.itemName}
          layout={this.state.layout}
          manager={this.manager}
          onBlur={this.handleBlur}
          onCancel={this.handleCancel}
          onChange={this.handleChange}
          onClick={this.handleClick}
          onFormClick={this.handleFormClick}
          onRemoveItem={this.handleRemoveItem}
          onResetForm={this.handleResetForm}
          onRowChange={this.handleRowChange}
          onSubmit={this.handleSubmit}
          onTagChange={this.handleTagChange}
          onWrapperClick={this.handleWrapperClick}
          options={this.props.options}
          originalRender={this.state.key === 0}
          persistTabs={this.props.persistTabs}
          previousStep={this.previousStep}
          process={this.state.process}
          readOnly={this.props.readOnly}
          report={this.compilePublicReport()}
          saving={this.state.saving}
          setInfo={this.setInfo}
          setWarning={this.setWarning}
          showHelp={this.props.showHelp}
          steps={this.layout.getSteps()}
          submitLabel={this.props.submitLabel}
        >
          {this.props.children}
        </FormEdit>
      </FormContext.Provider>
    );
  }
}

BlazeForm.defaultProps = {
  action: 'edit',
  children: null,
  customActions: [],
  className: '',
  exportFormat: 'object',
  focusFirstField: false,
  initialValues: {},
  itemId: { data: '' },
  itemName: '',
  layout: [],
  hideSubmit: false,
  hideCancel: false,
  hideReset: true,
  hideToolbar: false,
  manager: null,
  onBeforeSubmit: () => null,
  onBlur: () => {},
  onChange: () => {},
  onCancel: () => {},
  onLaunch: () => {},
  onResetForm: () => true,
  onSubmit: () => true,
  onTableFilter: null,
  onValidationReport: null,
  onUpdateRule: () => {},
  options: {},
  persistTabs: true,
  readOnly: false,
  renderInvalidLayout: () => null,
  renderLoading: () => null,
  resetOnSubmit: false,
  showHelp: true,
  submitAll: false,
  trackDirty: true,
  usePrefix: '',
  useTypedData: false,
};

BlazeForm.propTypes = {
  action: PropTypes.string,
  children: PropTypes.node,
  cancelLabel: PropTypes.string,
  customActions: PropTypes.array,
  className: PropTypes.string,
  exportFormat: PropTypes.oneOf(['array', 'object']),
  focusFirstField: PropTypes.bool,
  initialValues: PropTypes.object,
  itemId: PropTypes.object,
  itemName: PropTypes.string,
  hideCancel: PropTypes.bool,
  hideReset: PropTypes.bool,
  hideSubmit: PropTypes.bool,
  hideToolbar: PropTypes.bool,
  layout: PropTypes.array,
  manager: PropTypes.object,
  onBeforeSubmit: PropTypes.func,
  onBlur: PropTypes.func,
  onCancel: PropTypes.func,
  onChange: PropTypes.func,
  onLaunch: PropTypes.func,
  onResetForm: PropTypes.func,
  onSubmit: PropTypes.func,
  onTableFilter: PropTypes.func,
  onValidationReport: PropTypes.func,
  onUpdateRule: PropTypes.func,
  options: PropTypes.object,
  persistTabs: PropTypes.bool,
  readOnly: PropTypes.bool,
  renderInvalidLayout: PropTypes.func,
  renderLoading: PropTypes.func,
  resetOnSubmit: PropTypes.bool,
  showHelp: PropTypes.bool,
  submitAll: PropTypes.bool,
  submitLabel: PropTypes.string,
  usePrefix: PropTypes.string,
  useTypedData: PropTypes.bool,
  trackDirty: PropTypes.bool,
};

export default BlazeForm;
